/**
 * Thin client-side wrapper around gtag + Microsoft Clarity event APIs.
 *
 * Loaders live in @/components/analytics/Analytics — both providers attach
 * to `window` deferred (after `window.load` + `requestIdleCallback`), so
 * these functions are safe no-ops if called before that fires. Always
 * guarded against SSR / prerender where `window` is undefined.
 */

export type EventParams = Record<
  string,
  string | number | boolean | null | undefined
>;

const isBrowser = (): boolean => typeof window !== "undefined";

/**
 * Fire a custom event to both Google Analytics 4 (via gtag) and Microsoft
 * Clarity. GA4 accepts arbitrary parameters; Clarity events only take a
 * name, so params are dropped for Clarity. Use `setClarityTag` if you
 * need a Clarity-filterable dimension.
 */
export function trackEvent(name: string, params: EventParams = {}): void {
  if (!isBrowser()) return;
  window.gtag?.("event", name, params);
  window.clarity?.("event", name);
}

/**
 * Manually emit a GA4 `page_view`. Used for SPA route changes — gtag's
 * automatic page_view only fires on first script load.
 */
export function trackPageView(path: string, title?: string): void {
  if (!isBrowser()) return;
  window.gtag?.("event", "page_view", {
    page_path: path,
    page_title: title ?? document.title,
    page_location: window.location.href,
  });
}

/**
 * Set a custom dimension on Microsoft Clarity for the current session.
 * Useful for filtering session replays by attribute (e.g. landed-on page,
 * variant, user segment).
 */
export function setClarityTag(key: string, value: string): void {
  if (!isBrowser()) return;
  window.clarity?.("set", key, value);
}
